import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * CaptionSubTrack renders caption phrase blocks as segments on a sub-row
 * beneath the parent video/audio element in the timeline.
 * Supports inline editing that syncs with the properties panel.
 * Supports draggable left/right handles to adjust phrase timing,
 * and dragging the body of a phrase block to shift its timing.
 */
const subtractRange = (ranges, removeStart, removeEnd) => {
    const result = [];
    for (const r of ranges) {
        if (removeEnd <= r.start || removeStart >= r.end) {
            result.push(r);
        } else {
            if (removeStart > r.start) {
                result.push({ start: r.start, end: removeStart });
            }
            if (removeEnd < r.end) {
                result.push({ start: removeEnd, end: r.end });
            }
        }
    }
    return result;
};

const CaptionSubTrack = ({
    elements = [],
    duration,
    timelineWidth,
    onPhraseEdit,
    onPhraseTimingChange,
}) => {
    const [editingState, setEditingState] = useState({ elementId: null, phraseIndex: null });
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef(null);
    const trackRef = useRef(null);

    // Drag state for handle resizing and block moving
    const dragRef = useRef(null);

    // Keep latest callback refs to avoid stale closures in drag handlers
    const onPhraseTimingChangeRef = useRef(onPhraseTimingChange);
    onPhraseTimingChangeRef.current = onPhraseTimingChange;

    const durationRef = useRef(duration);
    durationRef.current = duration;

    const elementsRef = useRef(elements);
    elementsRef.current = elements;

    // Focus input when editing starts
    useEffect(() => {
        if (editingState.elementId !== null && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingState]);

    const elementsWithCaptions = elements.filter(el => el.componentData?.captions?.phrases?.length > 0);

    const FPS = 25;
    const MIN_PHRASE_DURATION_MS = 100; // Minimum phrase duration in ms

    /**
     * Convert a pixel X position on the track to a timeline time in seconds.
     */
    const pixelToTime = useCallback((pixelX) => {
        if (!timelineWidth || timelineWidth <= 0) return 0;
        return (pixelX / timelineWidth) * duration;
    }, [timelineWidth, duration]);

    /**
     * Handle mouse move during drag.
     * Uses refs to always read the latest values and avoid stale closures.
     */
    const handleDragMove = useCallback((e) => {
        const drag = dragRef.current;
        if (!drag) return;

        const deltaX = e.clientX - drag.startX;
        if (Math.abs(deltaX) < 2 && !drag.hasMoved) return;

        drag.hasMoved = true;

        // Convert pixel delta to time delta in milliseconds
        const currentDuration = durationRef.current;
        const deltaTimeSec = (deltaX / drag.trackWidth) * currentDuration;
        const deltaTimeMs = deltaTimeSec * 1000;

        // Read the LATEST element data from the elements prop (via ref) to get
        // up-to-date neighbor phrase positions after prior timing changes.
        const latestElement = elementsRef.current.find(el => el.id === drag.elementId);
        if (!latestElement) return;

        const captions = latestElement.componentData?.captions;
        if (!captions?.phrases) return;

        const phrases = captions.phrases;
        if (drag.phraseIndex >= phrases.length) return;

        // Get the element's timeline offset for clamping
        const inPoint = latestElement.timeline?.inPoint ?? 0;
        const outPoint = latestElement.timeline?.outPoint ?? (inPoint + (latestElement.componentData?.frameDuration ?? FPS * 10));
        const croppedInPoint = (latestElement.timeline?.croppedInPoint || 0) / FPS;
        const elementMediaStartMs = croppedInPoint * 1000;
        const elementMediaEndMs = elementMediaStartMs + ((outPoint - inPoint) / FPS) * 1000;

        let newStartMs = drag.originalStartMs;
        let newEndMs = drag.originalEndMs;

        if (drag.type === 'left') {
            // Dragging left handle: adjust start time
            newStartMs = drag.originalStartMs + deltaTimeMs;

            // Clamp: don't go before element media start
            newStartMs = Math.max(elementMediaStartMs, newStartMs);

            // Clamp: don't go past end minus minimum duration
            newStartMs = Math.min(newStartMs, drag.originalEndMs - MIN_PHRASE_DURATION_MS);

            // Clamp: don't overlap with previous phrase (use latest data)
            if (drag.phraseIndex > 0) {
                const prevPhrase = phrases[drag.phraseIndex - 1];
                newStartMs = Math.max(newStartMs, prevPhrase.end);
            }

            newStartMs = Math.round(newStartMs);
        } else if (drag.type === 'right') {
            // Dragging right handle: adjust end time
            newEndMs = drag.originalEndMs + deltaTimeMs;

            // Clamp: don't go past element media end
            newEndMs = Math.min(elementMediaEndMs, newEndMs);

            // Clamp: don't go before start plus minimum duration
            newEndMs = Math.max(newEndMs, drag.originalStartMs + MIN_PHRASE_DURATION_MS);

            // Clamp: don't overlap with next phrase (use latest data)
            if (drag.phraseIndex < phrases.length - 1) {
                const nextPhrase = phrases[drag.phraseIndex + 1];
                newEndMs = Math.min(newEndMs, nextPhrase.start);
            }

            newEndMs = Math.round(newEndMs);
        } else if (drag.type === 'move') {
            // Dragging the whole block: shift both start and end
            const phraseDuration = drag.originalEndMs - drag.originalStartMs;
            newStartMs = drag.originalStartMs + deltaTimeMs;
            newEndMs = newStartMs + phraseDuration;

            // Clamp: don't go before element media start
            if (newStartMs < elementMediaStartMs) {
                newStartMs = elementMediaStartMs;
                newEndMs = newStartMs + phraseDuration;
            }

            // Clamp: don't go past element media end
            if (newEndMs > elementMediaEndMs) {
                newEndMs = elementMediaEndMs;
                newStartMs = newEndMs - phraseDuration;
            }

            // Clamp: don't overlap with previous phrase (use latest data)
            if (drag.phraseIndex > 0) {
                const prevPhrase = phrases[drag.phraseIndex - 1];
                if (newStartMs < prevPhrase.end) {
                    newStartMs = prevPhrase.end;
                    newEndMs = newStartMs + phraseDuration;
                }
            }

            // Clamp: don't overlap with next phrase (use latest data)
            if (drag.phraseIndex < phrases.length - 1) {
                const nextPhrase = phrases[drag.phraseIndex + 1];
                if (newEndMs > nextPhrase.start) {
                    newEndMs = nextPhrase.start;
                    newStartMs = newEndMs - phraseDuration;
                }
            }

            newStartMs = Math.round(newStartMs);
            newEndMs = Math.round(newEndMs);
        }

        // Update the phrase timing via callback (always use latest ref)
        const callback = onPhraseTimingChangeRef.current;
        if (callback) {
            callback(drag.elementId, drag.phraseIndex, newStartMs, newEndMs);
        }
    }, []); // No deps needed - everything is read from refs

    /**
     * End drag operation.
     */
    const handleDragEnd = useCallback((e) => {
        const drag = dragRef.current;
        dragRef.current = null;
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);

        // If it was a move drag that didn't actually move, treat as click for editing
        if (drag && drag.type === 'move' && !drag.hasMoved) {
            // Read latest element data for the phrase text
            const latestElement = elementsRef.current.find(el => el.id === drag.elementId);
            const phrase = latestElement?.componentData?.captions?.phrases?.[drag.phraseIndex];
            if (phrase) {
                setEditingState({ elementId: drag.elementId, phraseIndex: drag.phraseIndex });
                setEditValue(phrase.text);
            }
        }
    }, [handleDragMove]);

    /**
     * Start dragging a handle (left or right edge) of a phrase block.
     */
    const handleHandleMouseDown = useCallback((e, elementId, phraseIndex, handleSide, phrase, element) => {
        e.stopPropagation();
        e.preventDefault();

        const trackRect = trackRef.current?.getBoundingClientRect();
        if (!trackRect) return;

        dragRef.current = {
            type: handleSide, // 'left' | 'right'
            elementId,
            phraseIndex,
            startX: e.clientX,
            trackLeft: trackRect.left,
            trackWidth: trackRect.width,
            originalStartMs: phrase.start,
            originalEndMs: phrase.end,
        };

        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
    }, [handleDragMove, handleDragEnd]);

    /**
     * Start dragging the body of a phrase block to shift its timing.
     */
    const handleBlockMouseDown = useCallback((e, elementId, phraseIndex, phrase, element) => {
        // Only start drag on left mouse button, and not if clicking on a handle
        if (e.button !== 0) return;
        if (e.target.dataset.captionHandle) return;

        e.stopPropagation();
        e.preventDefault();

        const trackRect = trackRef.current?.getBoundingClientRect();
        if (!trackRect) return;

        dragRef.current = {
            type: 'move',
            elementId,
            phraseIndex,
            startX: e.clientX,
            trackLeft: trackRect.left,
            trackWidth: trackRect.width,
            originalStartMs: phrase.start,
            originalEndMs: phrase.end,
            hasMoved: false,
        };

        document.addEventListener('mousemove', handleDragMove);
        document.addEventListener('mouseup', handleDragEnd);
    }, [handleDragMove, handleDragEnd]);

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [handleDragMove, handleDragEnd]);

    if (elementsWithCaptions.length === 0) return null;

    const handlePhraseClick = (e, elementId, index, text) => {
        e.stopPropagation();
        e.preventDefault();
        setEditingState({ elementId, phraseIndex: index });
        setEditValue(text);
    };

    const commitEdit = () => {
        if (editingState.elementId !== null && onPhraseEdit) {
            onPhraseEdit(editingState.elementId, editingState.phraseIndex, editValue);
        }
        setEditingState({ elementId: null, phraseIndex: null });
        setEditValue('');
    };

    const handleKeyDown = (e) => {
        // Stop ALL keyboard events from bubbling to the Timeline's shortcut handler
        e.stopPropagation();
        if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setEditingState({ elementId: null, phraseIndex: null });
            setEditValue('');
        }
    };

    return (
        <div
            ref={trackRef}
            className="h-8 relative flex items-center border-b border-ag-black-600 bg-ag-black-800/50 timeline-scroll override-scroll"
            style={{ width: `${timelineWidth}px` }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            {[...elementsWithCaptions]
                .sort((a, b) => {
                    const diff = (a.zIndex || 0) - (b.zIndex || 0);
                    if (diff !== 0) return diff;
                    return a.id.localeCompare(b.id);
                })
                .map((element, eIdx, sortedArray) => {
                    const captions = element.componentData.captions;
                    const inPoint = element.timeline?.inPoint ?? 0;
                    const outPoint = element.timeline?.outPoint ?? (inPoint + (element.componentData?.frameDuration ?? 25 * 10));
                    const elementStartTime = inPoint / FPS;
                    const elementDuration = (outPoint - inPoint) / FPS;
                    const elementEndTime = elementStartTime + elementDuration;

                    // Find all time ranges belonging to strictly higher priority elements
                    const higherPriorityRanges = [];
                    for (let hIdx = eIdx + 1; hIdx < sortedArray.length; hIdx++) {
                        const hElement = sortedArray[hIdx];
                        const hInPoint = hElement.timeline?.inPoint ?? 0;
                        const hOutPoint = hElement.timeline?.outPoint ?? (hInPoint + (hElement.componentData?.frameDuration ?? 25 * 10));
                        higherPriorityRanges.push({ start: hInPoint / FPS, end: hOutPoint / FPS });
                    }

                    return captions.phrases.map((phrase, i) => {
                        const phraseStartSec = phrase.start / 1000;
                        const phraseEndSec = phrase.end / 1000;

                        const croppedInPoint = (element.timeline?.croppedInPoint || 0) / FPS;
                        const phraseTimelineStart = elementStartTime + (phraseStartSec - croppedInPoint);
                        const phraseTimelineEnd = elementStartTime + (phraseEndSec - croppedInPoint);

                        // Don't show phrases outside the element's actual visible range on timeline
                        if (phraseTimelineEnd <= elementStartTime || phraseTimelineStart >= elementEndTime) {
                            return null;
                        }

                        // Check if this phrase's parent ELEMENT overlaps with ANY higher priority track
                        // Even partial intersection will hide the phrase ENTIRELY to avoid "traces"
                        const isOverlapped = higherPriorityRanges.some(r => {
                            return phraseTimelineStart < r.end && phraseTimelineEnd > r.start;
                        });

                        if (isOverlapped) {
                            return null;
                        }

                        // Clamp to element bounds for width drawing
                        const clampedStart = Math.max(phraseTimelineStart, elementStartTime);
                        const clampedEnd = Math.min(phraseTimelineEnd, elementEndTime);

                        if (clampedEnd <= clampedStart) return null;

                        const leftPct = (clampedStart / duration) * 100;
                        const widthPct = ((clampedEnd - clampedStart) / duration) * 100;

                        const isEditing = editingState.elementId === element.id && editingState.phraseIndex === i;
                        const isDragging = dragRef.current?.elementId === element.id && dragRef.current?.phraseIndex === i;

                        return (
                            <div
                                key={`${element.id}-${i}`}
                                className={`absolute h-[18px] top-[7px] rounded-[3px] flex items-center overflow-visible group
                                    ${isEditing
                                        ? 'bg-white border border-ag-green z-20'
                                        : isDragging
                                            ? 'bg-white border border-ag-green z-20'
                                            : 'bg-[#e8e8e8] hover:bg-white border border-[#ccc] z-10 hover:z-20'
                                    }`}
                                style={{
                                    left: `${leftPct}%`,
                                    width: `${Math.max(widthPct, 0.3)}%`,
                                    zIndex: Math.max(0, 10 + (element.layerProperties?.index || 0)),
                                    cursor: isEditing ? 'text' : 'grab',
                                }}
                                onMouseDown={(e) => {
                                    if (!isEditing) {
                                        handleBlockMouseDown(e, element.id, i, phrase, element);
                                    } else {
                                        e.stopPropagation();
                                    }
                                }}
                                title={`${phrase.text} (${(phrase.start / 1000).toFixed(2)}s - ${(phrase.end / 1000).toFixed(2)}s)`}
                            >
                                {/* Left drag handle */}
                                {!isEditing && (
                                    <div
                                        data-caption-handle="left"
                                        className="absolute left-0 top-0 bottom-0 w-[5px] cursor-col-resize z-30 flex items-center justify-center
                                            opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ marginLeft: '-2px' }}
                                        onMouseDown={(e) => handleHandleMouseDown(e, element.id, i, 'left', phrase, element)}
                                    >
                                        <div className="w-[3px] h-[12px] rounded-sm bg-ag-green" />
                                    </div>
                                )}

                                {/* Content area */}
                                <div className="flex-1 overflow-hidden min-w-0 px-1.5">
                                    {isEditing ? (
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onBlur={commitEdit}
                                            onKeyDown={handleKeyDown}
                                            onKeyUp={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => e.stopPropagation()}
                                            className="w-full h-full bg-transparent text-black text-[10px] outline-none"
                                        />
                                    ) : (
                                        <span className="text-[10px] text-[#222] truncate select-none font-medium block">
                                            {phrase.text}
                                        </span>
                                    )}
                                </div>

                                {/* Right drag handle */}
                                {!isEditing && (
                                    <div
                                        data-caption-handle="right"
                                        className="absolute right-0 top-0 bottom-0 w-[5px] cursor-col-resize z-30 flex items-center justify-center
                                            opacity-0 group-hover:opacity-100 transition-opacity"
                                        style={{ marginRight: '-2px' }}
                                        onMouseDown={(e) => handleHandleMouseDown(e, element.id, i, 'right', phrase, element)}
                                    >
                                        <div className="w-[3px] h-[12px] rounded-sm bg-ag-green" />
                                    </div>
                                )}
                            </div>
                        );
                    });
                })}
        </div>
    );
};

export default CaptionSubTrack;
